import { useQuery } from '@tanstack/react-query';
import { getTables, getViews } from '../../../database-objects/services/objectsService';
import { useDatabaseStore } from '../../../database-objects/store/databaseStore';
import type { DatabaseEngineId } from '../../../../core/engines';

export type ObjectType = 'table' | 'view';

export type TableInfo = {
  name: string;
  schema: string;
  database: string;
  objectType: ObjectType;
};

/**
 * F06 (Soporte3 B6) — which object-type listings failed on the last fetch.
 * `getTables`/`getViews` each report their own `error` independently, so one
 * can fail while the other succeeds; when that happens the query must not
 * silently drop the failed side and return a list that merely *looks*
 * complete. Attached as an extra property on the resolved array (see
 * `AvailableTablesData` below) rather than a new top-level query field, so
 * every existing consumer that destructures `data` as a plain `TableInfo[]`
 * (FkLookupPicker, ForeignKeyPopover, TableDesignerDialog, ...) keeps working
 * unchanged; only a caller that needs to know about a partial listing (the
 * table picker) has to read it.
 */
export type PartialListingInfo = {
  failedObjectTypes: ObjectType[];
};

export type AvailableTablesData = TableInfo[] & { partialListing?: PartialListingInfo };

type UseAvailableTablesOptions = {
  connectionId: string;
  database: string;
  engineId?: DatabaseEngineId;
  enabled?: boolean;
};

/**
 * Hook to fetch available tables and views for a connection and database.
 * First checks preloaded data from databaseStore, then falls back to API call.
 */
export function useAvailableTables({
  connectionId,
  database,
  engineId = 'mssql',
  enabled = true,
}: UseAvailableTablesOptions) {
  const getPreloadedObjects = useDatabaseStore((state) => state.getPreloadedObjects);

  return useQuery({
    queryKey: ['available-objects', connectionId, database, engineId],
    queryFn: async (): Promise<AvailableTablesData> => {
      // First, try to get objects from preloaded data
      const preloadedObjects = getPreloadedObjects(connectionId, database);
      if (preloadedObjects) {
        const tablesAndViews = preloadedObjects.filter(
          (obj) => obj.type === 'table' || obj.type === 'view'
        );
        return tablesAndViews.map((t) => ({
          name: t.name,
          schema: t.schema,
          database,
          objectType: t.type as ObjectType,
        }));
      }

      // Fall back to fetching from the API - fetch both tables and views
      const [tablesResult, viewsResult] = await Promise.all([
        getTables(connectionId, database, engineId),
        getViews(connectionId, database, engineId),
      ]);

      const objects: TableInfo[] = [];
      // F06 — track which listing(s) failed instead of silently dropping
      // them; a caller that only reads `data` as a plain array never notices
      // this, but the table picker surfaces it as a loud, retryable banner.
      const failedObjectTypes: ObjectType[] = [];

      if (tablesResult.error) {
        failedObjectTypes.push('table');
      } else {
        objects.push(
          ...tablesResult.tables.map((t) => ({
            name: t.name,
            schema: t.schema,
            database,
            objectType: 'table' as ObjectType,
          }))
        );
      }

      if (viewsResult.error) {
        failedObjectTypes.push('view');
      } else {
        objects.push(
          ...viewsResult.views.map((v) => ({
            name: v.name,
            schema: v.schema,
            database,
            objectType: 'view' as ObjectType,
          }))
        );
      }

      // Sort by name
      objects.sort((a, b) => a.name.localeCompare(b.name));

      if (failedObjectTypes.length > 0) {
        return Object.assign(objects, { partialListing: { failedObjectTypes } });
      }
      return objects;
    },
    enabled: enabled && !!connectionId && !!database,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
